import flatten from 'lodash/flatten';
import flattenDeep from 'lodash/flattenDeep';
import { useMemo } from 'react';
import parseSelections from '@/common/utils/widget/parseSelections';
import { getSelectionGroupItemByIndex } from '@/components/Editor/Form/utils';
import { DEFAULT_COLORS, getSelectionConfig, labelsSelectionConfig, legendsSelectionConfig, useData, checkIsGrouped } from '@/components/widget/visualizations/StackedBarChart';
import { overrideConfig } from '@/components/widget/visualizations/StackedBarChart/settings';
import { useBarStyles } from '@/components/widget/visualizations/internal/BarChart/stylesTheme';
import useSelections from '@/components/widget/misc/useSelections';
import useSelectionLabels from '@/components/widget/misc/useSelectionLabels';
import useSelectionLegends from '@/components/widget/misc/useSelectionLegends';
import useSubscribeWidgetResults from '@/common/components/widget/visualizations/hooks/useSubscribeWidgetResults';
import ColumnLabels from '@/components/Editor/Form/barChart/fields/ColumnLabels';
import BaseChartOptions from '@/components/Editor/Form/barChart/fields/ChartOptions';
import { jsx as _jsx } from "react/jsx-runtime";

const WidgetEditFormStackedBarChartOptions = _ref => {
  let {
    widgetId,
    sectionId,
    reportingWidgetId,
    config,
    pillVariant,
    onChange,
    onEditSelections
  } = _ref;
  const {
    barThemeStyles,
    themeCtx: {
      colorPallet: defaultColorPallet = DEFAULT_COLORS
    }
  } = useBarStyles(config);
  const configWithOverrides = useMemo(() => overrideConfig(config), [config]);
  const {
    columnLabels = [],
    selections,
    labelsSelections,
    legendsSelections,
    seriesOptions = [],
    selectionsStrategy = 'default',
    selectionsLabeled = true,
    collectionOptions = [],
    globalSeriesOptions,
    colorPallet = defaultColorPallet,
    chartType = 'vertical',
    xAxisTemplate,
    yAxisTemplate
  } = configWithOverrides; // Get current selection results

  const results = useSubscribeWidgetResults(reportingWidgetId); // Apply selections to results

  const {
    selectionResults,
    resultsUpdated
  } = useSelections({
    results,
    selections: selections || '',
    selectionsLabeled,
    selectionConfig: getSelectionConfig(selectionsStrategy)
  }); // Get selection labels

  const labelsTemplate = chartType === 'vertical' ? yAxisTemplate : xAxisTemplate;
  const {
    selectionLabels,
    labelsSelectionsResult
  } = useSelectionLabels({
    results,
    resultsUpdated,
    labelsTemplate: labelsTemplate || (globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.labelsTemplate),
    labelsSelections,
    labelsSelectionConfig
  }); // Get selection legends

  const {
    selectionLegends
  } = useSelectionLegends({
    results,
    resultsUpdated,
    legendsSelections,
    legendsSelectionConfig
  }); // Combine results with rendering styles

  const {
    chartData
  } = useData({
    results: selectionResults,
    selectionLabels,
    selectionLegends,
    seriesOptions,
    collectionOptions,
    colorPallet,
    chartType,
    selectionsStrategy,
    columnLabels: configWithOverrides.columnLabels,
    hasLegend: configWithOverrides.hasLegend,
    themeStyles: barThemeStyles
  });
  const selectionCollections = useMemo(() => {
    if (typeof selections === 'string') {
      return parseSelections(selections);
    }

    return selections || [];
  }, [selections]);
  const columnCount = useMemo(() => {
    if (selectionsStrategy === 'categories') {
      let maxSeriesCount = 0;
      selectionResults.forEach(selection => {
        selection.forEach(group => {
          maxSeriesCount = Math.max(maxSeriesCount, group.data.length);
        });
      });
      return maxSeriesCount;
    }

    if (selectionsStrategy === 'stacked') {
      return selectionResults.length;
    }

    return flatten(selectionCollections).length;
  }, [selectionCollections, selectionsStrategy, selectionResults]);
  const isGrouped = checkIsGrouped(selectionsStrategy, selectionResults);
  const exampleValue = useMemo(() => {
    const exampleSelection = getSelectionGroupItemByIndex(flattenDeep(selectionResults), 0);
    return typeof exampleSelection.value !== 'undefined' ? exampleSelection.value : 10;
  }, [selectionResults]);
  const exampleLabel = useMemo(() => {
    const exampleSelection = getSelectionGroupItemByIndex(flattenDeep(labelsSelectionsResult), 0);
    return typeof exampleSelection.value !== 'undefined' ? exampleSelection.value : 'N/A';
  }, [labelsSelectionsResult]);
  return /*#__PURE__*/_jsx(BaseChartOptions, {
    widgetId: widgetId,
    sectionId: sectionId,
    config: configWithOverrides,
    hasLegend: configWithOverrides.hasLegend ?? true,
    legendsCount: chartData.legendsCount,
    isGrouped: isGrouped,
    dataBoundaries: chartData.dataBoundaries,
    hasTrendline: false,
    exampleValue: exampleValue,
    exampleLabel: exampleLabel,
    pillVariant: pillVariant,
    onChange: onChange,
    onEditSelections: onEditSelections,
    children: /*#__PURE__*/_jsx(ColumnLabels, {
      isColumn: chartType === 'vertical',
      nameSpace: `${widgetId}-${sectionId}`,
      labels: columnLabels,
      columnCount: columnCount,
      openButtonVariant: pillVariant,
      onChange: onChange
    })
  });
};

export default WidgetEditFormStackedBarChartOptions;